/**
 * SCHEDULED MENU PRICING ONLINE — the pieces the storefront AND the server's
 * cart re-pricing must agree on, so a cart the storefront priced is a cart the
 * server accepts (Bar v2 Part D2).
 *
 *   - `resolveOnlineScheduledPaths`: `resolveScheduledPaths` under the ONLINE
 *     storefront's gates — online channel, the cart's fulfillment, the website
 *     builder's hidden sections, per-parent online edges. The storefront page
 *     (`@order-up/client` `scheduledItemPaths`) and `repriceCart` both call
 *     this; if they disagreed, a guest confirming the storefront's price would
 *     be refused by the server forever.
 *   - `hiddenWebsiteMenuGroupIds`: which groups the builder hid — the one
 *     reading of `websiteConfig.menuSections`.
 *   - the guest's words: why a price moved ("Happy Hour ended — Tito's is now
 *     $9.00") and why an item cannot be ordered now ("Tito's is only available
 *     during Happy Hour (Mon–Thu 3–6 PM)"). Written once so the storefront's
 *     notice and the server's refusal say the same thing.
 *
 * Pure: no Firestore, no clock (`now` is injected).
 */

import type { LocationWebsiteConfig } from '../locations/Location';
import type { IMenuGroup, IMenuGroupDaySchedule, MenuGroupDayKey } from './MenuGroup';
import { MENU_GROUP_DAY_KEYS } from './MenuGroup';
import { isSubgroupEdgeAvailableOnline } from './channelVisibility';
import {
  isMenuGroupAvailableForOnlineOrdering,
  isMenuGroupChannelEnabled,
  salesChannelForCartOrderType,
} from './menuGroupAvailability';
import { MINUTES_PER_DAY, resolveDaySchedule, weeklyCovers, type IScheduleInterval } from './menuGroupSchedule';
import { enumerateItemMenuPaths } from './menuTree';
import { resolveScheduledPaths, scheduledGroupOnPath } from './scheduledMenuPath';

/**
 * The group ids the website builder hid (`websiteConfig.menuSections` entries
 * with `visible === false`). Absent config, or no entry, means visible.
 */
export function hiddenWebsiteMenuGroupIds(
  config: Pick<LocationWebsiteConfig, 'menuSections'> | null | undefined,
): Set<number> {
  const hidden = new Set<number>();
  for (const section of config?.menuSections ?? []) {
    if (section.visible === false && section.menuGroupId != null) hidden.add(section.menuGroupId);
  }
  return hidden;
}

export interface OnlineScheduledPathsParams {
  /** The storefront's root menu; omitted, every root counts. */
  readonly rootId?: number;
  readonly menuGroups: readonly IMenuGroup[];
  /** Groups the website builder hid (`hiddenWebsiteMenuGroupIds`). */
  readonly hiddenSectionIds?: ReadonlySet<number>;
  /** The cart's fulfillment — a pickup cart may not price through a delivery-only group. */
  readonly orderType: 'pickup' | 'delivery' | 'dineIn';
  readonly now: Date;
  /** The restaurant's IANA zone (`resolveScheduleTimeZone`). */
  readonly timeZone: string | null | undefined;
  /** Items to resolve; default every item an open route lists. */
  readonly itemIds?: readonly number[] | 'all';
}

/** itemId → the ROOT→LEAF route that prices it now online. Unreachable items are absent. */
export function resolveOnlineScheduledPaths({
  rootId,
  menuGroups,
  hiddenSectionIds,
  orderType,
  now,
  timeZone,
  itemIds = 'all',
}: OnlineScheduledPathsParams): Map<number, number[]> {
  const fulfillment = salesChannelForCartOrderType(orderType);
  return resolveScheduledPaths(itemIds, menuGroups, now, timeZone, {
    ...(rootId != null ? { rootId } : {}),
    enterGroup: (g) =>
      isMenuGroupAvailableForOnlineOrdering(g, now, timeZone) &&
      isMenuGroupChannelEnabled(g, fulfillment) &&
      !(hiddenSectionIds?.has(g.menuGroupId as number) ?? false),
    enterEdge: ({ override }) => isSubgroupEdgeAvailableOnline(override ?? undefined, now, timeZone),
  });
}

/** Why a cart line's price moved between the cart and checkout. */
export type ScheduledPriceChangeReason =
  | { readonly kind: 'ended'; readonly groupName: string }
  | { readonly kind: 'started'; readonly groupName: string }
  | { readonly kind: 'changed' };

/**
 * Why the route that prices a line now differs from the one it was priced on:
 * the time-limited group it passed through closed (`ended`), a time-limited
 * group now prices it (`started`), or neither (`changed` — the menu itself
 * moved). A line with no recorded path can only be `started` or `changed`.
 */
export function scheduledPriceChangeReason(
  pricedOn: readonly number[] | undefined,
  pricesNow: readonly number[],
  menuGroups: readonly IMenuGroup[],
): ScheduledPriceChangeReason {
  const before = pricedOn ? scheduledGroupOnPath(pricedOn, menuGroups) : null;
  const after = scheduledGroupOnPath(pricesNow, menuGroups);
  if (before != null && before.menuGroupId !== after?.menuGroupId) {
    return { kind: 'ended', groupName: before.name };
  }
  if (after != null && after.menuGroupId !== before?.menuGroupId) {
    return { kind: 'started', groupName: after.name };
  }
  return { kind: 'changed' };
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** "$9.00" — the guest-facing money format of every scheduled-pricing sentence. */
export function formatScheduledPrice(amount: number): string {
  return usd.format(amount);
}

/** "Happy Hour ended — Tito's is now $9.00". */
export function describeScheduledPriceChange(change: {
  readonly name: string;
  readonly newUnitPrice: number;
  readonly reason: ScheduledPriceChangeReason;
}): string {
  const now = formatScheduledPrice(change.newUnitPrice);
  switch (change.reason.kind) {
    case 'ended':
      return `${change.reason.groupName} ended — ${change.name} is now ${now}`;
    case 'started':
      return `${change.reason.groupName} started — ${change.name} is now ${now}`;
    case 'changed':
      return `${change.name} is now ${now}`;
  }
}

const DAY_SHORT: Record<MenuGroupDayKey, string> = {
  sunday: 'Sun',
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
};

/** "3", "3:30", "11" with its meridiem kept separate so a shared one prints once. */
function clockParts(minute: number): { text: string; meridiem: 'AM' | 'PM' } {
  const h24 = Math.floor(minute / 60) % 24;
  const m = minute % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { text: m === 0 ? `${h12}` : `${h12}:${String(m).padStart(2, '0')}`, meridiem: h24 < 12 ? 'AM' : 'PM' };
}

/** "3–6 PM", "11 AM–2 PM" — authored start through authored (inclusive) end. */
function formatWindow(interval: IScheduleInterval): string {
  const start = clockParts(interval.start);
  const end = clockParts(interval.end - 1);
  return start.meridiem === end.meridiem
    ? `${start.text}–${end.text} ${end.meridiem}`
    : `${start.text} ${start.meridiem}–${end.text} ${end.meridiem}`;
}

/** "Mon–Thu", or "Mon, Wed, Fri" when the open days are not one run (week order Sun..Sat). */
function formatDays(days: readonly MenuGroupDayKey[]): string {
  const idx = days.map((d) => MENU_GROUP_DAY_KEYS.indexOf(d));
  const run = idx.every((v, i) => i === 0 || v === idx[i - 1]! + 1);
  if (days.length === 7) return 'every day';
  if (run && days.length >= 3) return `${DAY_SHORT[days[0]!]}–${DAY_SHORT[days[days.length - 1]!]}`;
  return days.map((d) => DAY_SHORT[d]).join(', ');
}

/**
 * "Mon–Thu 3–6 PM" — the group's day-of-week schedule in one phrase, or `null`
 * when it does not reduce to one: the open days must share ONE identical
 * window that ends the same day (an overnight window, two windows a day, or
 * different hours per day do not summarise honestly).
 */
export function describeScheduledAvailability(group: IMenuGroup): string | null {
  const da = group.daysAvailable;
  if (da == null || !da.isActive) return null;
  const week = Object.fromEntries(
    MENU_GROUP_DAY_KEYS.map((day) => [day, resolveDaySchedule(da, day)]),
  ) as Record<MenuGroupDayKey, IMenuGroupDaySchedule>;
  const covers = weeklyCovers(week);
  const open = MENU_GROUP_DAY_KEYS.filter((d) => covers[d].length > 0);
  if (open.length === 0) return null;
  const first = covers[open[0]!];
  if (first.length !== 1) return null;
  const window = first[0]!;
  const same = open.every((d) => {
    const c = covers[d];
    return c.length === 1 && c[0]!.start === window.start && c[0]!.end === window.end;
  });
  if (!same) return null;
  const allDay = window.start === 0 && window.end === MINUTES_PER_DAY;
  if (allDay) return open.length === 7 ? null : formatDays(open);
  return `${formatDays(open)} ${formatWindow(window)}`;
}

/**
 * Why `itemName` cannot be ordered online right now:
 * "Tito's is only available during Happy Hour (Mon–Thu 3–6 PM)" when a route
 * to it passes through a time-limited group whose schedule summarises, else
 * "Tito's is not available right now".
 *
 * @param rootId the storefront's root menu, when known — the routes a guest on
 *               that menu could have taken.
 */
export function describeItemUnavailableNow(
  itemName: string,
  itemId: number,
  menuGroups: readonly IMenuGroup[],
  rootId?: number,
): string {
  const paths = enumerateItemMenuPaths(menuGroups, itemId)
    .map((p) => p.menuGroupPath)
    .filter((p) => rootId == null || p[0] === rootId);
  for (const path of paths) {
    const group = scheduledGroupOnPath(path, menuGroups);
    const when = group ? describeScheduledAvailability(group) : null;
    if (group && when) return `${itemName} is only available during ${group.name} (${when})`;
  }
  return `${itemName} is not available right now`;
}
